import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ThemeType = 'red' | 'blue' | 'green' | 'gray' | 'purple';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<ThemeType>('red');
  public currentTheme$ = this.currentThemeSubject.asObservable();

  constructor() {
    this.loadThemeFromStorage();
  }

  /**
   * Get the current theme
   */
  getCurrentTheme(): ThemeType {
    return this.currentThemeSubject.value;
  }

  /**
   * Set a new theme
   */
  setTheme(theme: ThemeType): void {
    this.currentThemeSubject.next(theme);
    this.applyTheme(theme);
    this.saveThemeToStorage(theme);
  }

  /**
   * Apply theme to the document
   */
  private applyTheme(theme: ThemeType): void {
    // Remove all existing theme classes
    document.documentElement.removeAttribute('data-theme');
    
    // Apply new theme
    if (theme !== 'red') { // red is the default theme
      document.documentElement.setAttribute('data-theme', theme);
    }
  }

  /**
   * Save theme to localStorage
   */
  private saveThemeToStorage(theme: ThemeType): void {
    try {
      localStorage.setItem('kinvyt-theme', theme);
    } catch (error) {
      console.warn('Could not save theme to localStorage:', error);
    }
  }

  /**
   * Load theme from localStorage
   */
  private loadThemeFromStorage(): void {
    try {
      const savedTheme = localStorage.getItem('kinvyt-theme') as ThemeType;
      if (savedTheme && this.isValidTheme(savedTheme)) {
        this.setTheme(savedTheme);
      } else {
        this.setTheme('red'); // Default theme
      }
    } catch (error) {
      console.warn('Could not load theme from localStorage:', error);
      this.setTheme('red'); // Default theme
    }
  }

  /**
   * Check if theme is valid
   */
  private isValidTheme(theme: string): theme is ThemeType {
    return ['red', 'blue', 'green', 'gray', 'purple'].includes(theme);
  }

  /**
   * Get available themes
   */
  getAvailableThemes(): { value: ThemeType; label: string; color: string }[] {
    return [
      { value: 'red', label: 'Red', color: '#bf1a47' },
      { value: 'blue', label: 'Blue', color: '#0056b3' },
      { value: 'green', label: 'Green', color: '#2e7d32' },
      { value: 'gray', label: 'Gray', color: '#6c757d' },
      { value: 'purple', label: 'Purple', color: '#6a0572' }
    ];
  }

  /**
   * Toggle between themes (cycles through available themes)
   */
  toggleTheme(): void {
    const themes: ThemeType[] = ['red', 'blue', 'green', 'gray', 'purple'];
    const currentIndex = themes.indexOf(this.getCurrentTheme());
    const nextIndex = (currentIndex + 1) % themes.length;
    this.setTheme(themes[nextIndex]);
  }
} 